<template>
    <picture ref="rootRef">
        <source
            type="image/webp"
            :srcset="webpSrcset"
            v-if="displayWebp && !isInStorybook"
            :width="width"
            :height="height"
            :media="media"
        />
        <source :type="mimeType" :srcset="imgSrcset" :width="width" :height="height" :media="media" />
        <img
            :src="src"
            :alt="alt"
            :class="cssClass"
            :width="width"
            :height="height"
            :loading="isLazyload ? 'lazy' : null"
            :fetch-priority="loadingPriority"
        />
    </picture>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { WebpHelper } from "@sazka/utils/webp-helper";
import { WebpImageExpose } from "../types/webp-image-reference";

const props = withDefaults(
    defineProps<{
        src: string;
        cssClass?: Array<string> | string | Record<string, any>;
        alt: string;
        width?: number | string;
        height?: number | string;
        isLazyload?: boolean;
        loadingPriority?: "high" | "low" | "auto";
        /**
         * Specify a media query for the source
         */
        media?: string;
        /**
         * If true, 2x image will be added
         * url has to be in format: /path/to/image@2x.png (or jpg)
         */
        get2x?: boolean;
        isInStorybook?: boolean;
    }>(),
    {
        isLazyload: false,
        get2x: false,
        isStorybook: false,
    }
);

const rootRef = ref<HTMLPictureElement>();
defineExpose<WebpImageExpose>({
    rootRef,
});

const is2xEnabled = computed(() => props.get2x && !/@2x./.test(props.src));

const get2xSrc = (src: string): string => {
    // appends @2x to the end of the image name
    const srcUrl = new URL(src, window.location.origin);
    srcUrl.pathname = srcUrl.pathname.replace(".", "@2x.");
    return srcUrl.toString();
};

const getSrcset = (src1x: string, src2x: string): string => {
    return `${src1x} 1x, ${src2x} 2x`;
};

const imgSrcset = computed(() => {
    if (!props.src) {
        return "";
    }
    if (props.src.includes("|")) {
        const parts = props.src.split("|");
        return getSrcset(parts[0], parts[1]);
    }
    return is2xEnabled.value ? getSrcset(props.src, get2xSrc(props.src)) : props.src;
});

const webpSrcset = computed(() => {
    if (!props.src) {
        return "";
    }

    let src = props.src;

    //for images with saved width and height
    if (src.includes(";;")) {
        const parts = src.split(";;");
        src = parts[1];
    }

    const webpSrc = WebpHelper.webpUrl(src);

    if (webpSrc.includes("|")) {
        const parts = webpSrc.split("|");
        return getSrcset(parts[0], parts[1]);
    }

    return is2xEnabled.value ? getSrcset(webpSrc, get2xSrc(webpSrc)) : webpSrc;
});

const pattern = /(?:\.([^.]+))?$/;
const extension = pattern.exec(props.src || "")[1];

const mimeType = computed(() => {
    if (!extension) {
        return "";
    }

    return `image/${extension}`;
});

const displayWebp = computed(() => {
    return extension !== "svg";
});
</script>
